import React, { useState, useEffect, useCallback } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  ShieldCheck, Save, Plug, Clock, Loader2, AlertTriangle, CheckCircle2, Lock, KeyRound, Sparkles, Copy,
} from 'lucide-react';
import CpscPill from './CpscPill';

/**
 * CpscSettings — CHA-167. Admin settings for the CPSC eFiling integration, under Imports.
 *
 * Stores config REFERENCES only (certifierId, collectionId) via the `saveCpscConfig` callable;
 * the API token + secret are provisioned out-of-band in Secret Manager (decision B1) and are NEVER
 * entered or displayed here. `testCpscConnection` verifies the stored config against the CPSC API
 * and surfaces the company/collection/roles + token expiration without echoing credentials.
 *
 * Read-back of the saved config comes straight from Firestore `/cpsc_config/{tenantId}` (rules allow
 * authenticated read; writes are server-callable-only). Server enforces admin gating
 * (requireTenantAdminOrPlatformAdmin); this component also hides the surface from non-admins.
 */
const s = (v) => String(v ?? '').trim();

// CHA-544 (D) — the Manual CoC upload fixer's model choices. The server enforces the same allowlist
// (functions/cpscAiModels.js: priced models, never Opus) and rejects anything else.
const UPLOAD_FIX_OPTIONS = ['claude-haiku-5-5', 'claude-sonnet-5-5'];
const UPLOAD_FIX_DEFAULTS = { model: 'claude-haiku-5-5', escalationModel: 'claude-sonnet-5-5' };

const ERROR_COPY = {
  not_configured: 'Save the Certifier ID + Collection ID first.',
  secret_missing: 'CPSC credentials are not provisioned in Secret Manager — see the setup runbook (one-time gcloud step).',
  unauthorized: 'CPSC rejected the credentials. Verify the token + secret provisioned via gcloud.',
  certifier_mismatch: 'The saved Certifier ID does not match what CPSC returned — check the value.',
  collection_mismatch: "The saved Collection ID isn't in this certifier's collections — check the value.",
  network_error: 'CPSC API was unreachable. Retry in a moment.',
  cpsc_error: 'CPSC API returned an error.',
  bad_response: 'CPSC returned an unexpected response.',
  callable_error: 'The connection test could not run.',
};

// Token-expiration → status pill (Ron: yellow ≤30 days, red expired). Defensive on the raw value.
function expiryInfo(raw) {
  const str = s(raw);
  if (!str) return null;
  const t = Date.parse(str);
  if (Number.isNaN(t)) return { label: `token expires ${str}`, tone: 'slate' };
  const days = Math.floor((t - Date.now()) / 86400000);
  if (days < 0) return { label: 'token expired', tone: 'red' };
  if (days <= 30) return { label: `token expires in ${days}d`, tone: 'amber' };
  return { label: `token expires in ${days}d`, tone: 'green' };
}

export default function CpscSettings({ userProfile } = {}) {
  const isPlatformOwner = userProfile?.role === 'platform_owner';
  const isAdmin = isPlatformOwner || userProfile?.role === 'admin';
  // Tenant resolution: tenant-scoped admins act on their own tenant; platform_owner is tenantless and
  // targets the single live tenant as the Path-1 default. Derived from userProfile (App.jsx sets
  // profile.tenantId from custom claims) — not a bare hardcode.
  const activeTenantId = userProfile?.tenantId || 'inmocean';

  const [certifierId, setCertifierId] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [lastVerifiedAt, setLastVerifiedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);   // { ok, ...details } | { ok:false, code, message }
  // CHA-262 — AI decoder (non-secret config; the key itself is provisioned out-of-band, never entered here)
  const [llmEgress, setLlmEgress] = useState(false);
  const [savedLlmEgress, setSavedLlmEgress] = useState(false); // persisted value — drives the status pill (CodeRabbit)
  const [llmModel, setLlmModel] = useState('');
  // CHA-544 (D) — the upload fixer's models (server allowlist in functions/cpscAiModels.js; never Opus)
  const [fixModel, setFixModel] = useState(UPLOAD_FIX_DEFAULTS.model);
  const [fixEscalation, setFixEscalation] = useState(UPLOAD_FIX_DEFAULTS.escalationModel);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiMsg, setAiMsg] = useState(null);
  const [cmdCopied, setCmdCopied] = useState(false);

  const payload = useCallback((extra = {}) => (isPlatformOwner ? { tenantId: activeTenantId, ...extra } : { ...extra }), [isPlatformOwner, activeTenantId]);

  // Read the saved config straight from Firestore (refs only — no secret values live here).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(getFirestore(), 'cpsc_config', activeTenantId));
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data();
          setCertifierId(s(d.certifierId));
          setCollectionId(s(d.collectionId));
          setConfigured(!!(d.certifierId && d.collectionId));
          setLastVerifiedAt(d.lastVerifiedAt?.toDate?.() || null);
          setLlmEgress(d.llmEgressConsent === true); // CHA-262 — default OFF
          setSavedLlmEgress(d.llmEgressConsent === true);
          setLlmModel(s(d.llmModel));
          const fix = d.llmModels?.cpsc_upload_fix || {};
          setFixModel(UPLOAD_FIX_OPTIONS.includes(fix.model) ? fix.model : UPLOAD_FIX_DEFAULTS.model);
          setFixEscalation(UPLOAD_FIX_OPTIONS.includes(fix.escalationModel) ? fix.escalationModel : UPLOAD_FIX_DEFAULTS.escalationModel);
        }
      } catch (error) {
        // Don't swallow — surface so the operator knows saved config couldn't load (§8.3 / CodeRabbit).
        console.error('Failed to load CPSC config', error);
        if (!cancelled) setSaveMsg({ ok: false, text: 'Could not load saved CPSC settings. Retry before saving changes.' });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTenantId]);

  const runTest = useCallback(async () => {
    setTesting(true); setResult(null);
    try {
      const fn = httpsCallable(getFunctions(), 'testCpscConnection');
      const res = await fn(payload());
      setResult(res?.data || { ok: false, code: 'bad_response' });
      if (res?.data?.ok) setLastVerifiedAt(new Date());
    } catch (e) {
      setResult({ ok: false, code: 'callable_error', message: e?.message });
    } finally {
      setTesting(false);
    }
  }, [payload]);

  const save = useCallback(async () => {
    setSaving(true); setSaveMsg(null); setResult(null);
    let ok = false;
    try {
      const fn = httpsCallable(getFunctions(), 'saveCpscConfig');
      await fn(payload({ certifierId: s(certifierId), collectionId: s(collectionId) }));
      setConfigured(true);
      ok = true;
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
    if (ok) await runTest(); // auto-verify the just-saved IDs against the CPSC API (Ron #4)
  }, [certifierId, collectionId, payload, runTest]);

  // CHA-262 — extracted like save/runTest (CodeRabbit): named handler + error logging for observability.
  const saveAiConfig = useCallback(async () => {
    setAiSaving(true); setAiMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'saveCpscAiConfig');
      const res = await fn(payload({ llmEgressConsent: llmEgress, llmModel: s(llmModel), uploadFixModel: fixModel, uploadFixEscalationModel: fixEscalation }));
      setSavedLlmEgress(res.data?.llmEgressConsent === true);
      const fix = res.data?.uploadFix;
      setAiMsg({ ok: true, text: `AI decoder ${res.data?.llmEgressConsent ? 'enabled' : 'disabled'} (model: ${res.data?.llmModel}${fix ? `; upload fixer: ${fix.model}, retry ${fix.escalationModel}` : ''}).` });
    } catch (e) {
      console.error('Failed to save CPSC AI config', e);
      setAiMsg({ ok: false, text: e?.message || 'Could not save the AI decoder settings.' });
    } finally { setAiSaving(false); }
  }, [llmEgress, llmModel, fixModel, fixEscalation, payload]);

  if (!isAdmin) {
    return (
      <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">
        CPSC Settings is restricted to administrators.
      </div>
    );
  }

  const busy = saving || testing;
  const expiry = result?.ok ? expiryInfo(result.tokenExpiration) : null;
  // CHA-262 — the one-time key-provisioning command (out-of-band per CHA-167 B1; the key never transits the
  // app). Includes the per-secret secretAccessor grant for the Functions runtime SA — without it,
  // getTenantSecret is permission-denied and every decode falls back "key missing" (Codex P2; the SA + role
  // mirror docs/runbooks/cpsc-secrets-setup.md step 3).
  const llmSecretName = `TENANT_${activeTenantId.toUpperCase()}_LLM_API_KEY`;
  const llmKeyCmd = `S=${llmSecretName}; (printf '%s' "$YOUR_LLM_API_KEY" | gcloud secrets create "$S" --project=actualizer2 --replication-policy=automatic --data-file=- 2>/dev/null || printf '%s' "$YOUR_LLM_API_KEY" | gcloud secrets versions add "$S" --project=actualizer2 --data-file=-) && gcloud secrets add-iam-policy-binding "$S" --member="serviceAccount:169010327586-compute@developer.gserviceaccount.com" --role="roles/secretmanager.secretAccessor" --project=actualizer2`;


  return (
    <div className="font-plex text-carbon-ink space-y-4 max-w-2xl">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-semibold text-carbon-ink">CPSC Settings</h3>
          {loaded && <CpscPill tone={configured ? 'green' : 'slate'} icon={configured ? CheckCircle2 : AlertTriangle}>{configured ? 'Configured' : 'Not configured'}</CpscPill>}
          {result?.ok && <CpscPill tone="green" icon={KeyRound}>Secrets accessible</CpscPill>}
          {result?.code === 'secret_missing' && <CpscPill tone="red" icon={KeyRound}>Secrets missing</CpscPill>}
          {expiry && <CpscPill tone={expiry.tone} icon={Clock}>{expiry.label}</CpscPill>}
          {lastVerifiedAt && <span className="font-plex-mono text-[10.5px] text-carbon-gray-50">last verified {lastVerifiedAt.toLocaleString()}</span>}
        </div>
        <p className="text-sm text-carbon-gray-70">
          Certifier ID + Collection ID for the CPSC eFiling Product Registry. The API token &amp; secret are
          provisioned separately in Secret Manager (not entered here) and are never displayed.
        </p>
      </div>

      {isPlatformOwner && (
        <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-2">
          Acting as <strong>platform_owner</strong> against tenant: <strong>{activeTenantId}</strong>
        </div>
      )}

      {loaded && !configured && (
        <div className="text-sm text-carbon-gray-70 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-orange-40 px-4 py-3">
          <strong className="text-carbon-orange-70">Not configured yet.</strong> Before this works, an admin must provision the CPSC token &amp;
          secret in Secret Manager (one-time gcloud step) — see{' '}
          <code>docs/runbooks/cpsc-secrets-setup.md</code>. Then enter the Certifier ID + Collection ID below.
        </div>
      )}

      <div className="bg-carbon-white border border-carbon-border p-4 space-y-3">
        <label className="block">
          <span className="font-plex-mono text-[10px] uppercase tracking-[0.1em] font-medium text-carbon-gray-70">Certifier ID</span>
          <input
            type="text" value={certifierId} onChange={(e) => setCertifierId(e.target.value)}
            placeholder="e.g. InmoceanGroupLLC" disabled={busy}
            className="mt-1 w-full text-sm border border-carbon-gray-30 bg-carbon-gray-10 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60"
          />
        </label>
        <label className="block">
          <span className="font-plex-mono text-[10px] uppercase tracking-[0.1em] font-medium text-carbon-gray-70">Collection ID</span>
          <input
            type="text" value={collectionId} onChange={(e) => setCollectionId(e.target.value)}
            placeholder="e.g. 93fd4ed6-e508-41db-8717-9e189ab0e510" disabled={busy}
            className="mt-1 w-full text-sm border border-carbon-gray-30 bg-carbon-gray-10 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60"
          />
        </label>

        <div className="flex items-center gap-2 flex-wrap pt-1">
          <button
            onClick={save} disabled={busy || !s(certifierId) || !s(collectionId)}
            className="flex items-center gap-2 px-4 py-2 bg-carbon-blue-60 text-white text-sm font-semibold hover:bg-carbon-blue-70 disabled:opacity-50"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Configuration
          </button>
          <button
            onClick={runTest} disabled={busy || !configured}
            title={configured ? 'Verify credentials + collection against the CPSC API' : 'Save the configuration first'}
            className="flex items-center gap-2 px-4 py-2 border border-carbon-gray-30 text-carbon-gray-80 text-sm hover:bg-carbon-gray-10 disabled:opacity-50"
          >
            {testing ? <Loader2 size={15} className="animate-spin" /> : <Plug size={15} />} Test Connection
          </button>
          <button
            onClick={runTest} disabled={busy || !configured}
            title="Re-check the token expiration via the CPSC API"
            className="flex items-center gap-2 px-3 py-2 border border-carbon-gray-30 text-carbon-gray-80 text-sm hover:bg-carbon-gray-10 disabled:opacity-50"
          >
            <Clock size={15} /> Check Token Expiration
          </button>
        </div>

        <div className="text-[11px] text-carbon-gray-50 flex items-start gap-1">
          <Lock size={11} className="mt-0.5 shrink-0" />
          <span>
            Token &amp; secret live in Secret Manager (<code>TENANT_{activeTenantId.toUpperCase()}_CPSC_API_*</code>),
            provisioned via gcloud — see <code>docs/runbooks/cpsc-secrets-setup.md</code>. Test Connection verifies
            collections and token expiration in one call.
          </span>
        </div>
      </div>

      {saveMsg && (
        <div className={`text-sm px-3 py-2 flex items-start gap-1 border ${saveMsg.ok ? 'text-carbon-gray-80 bg-carbon-green-10 border-carbon-green-50' : 'text-carbon-red-60 bg-carbon-white border-carbon-border border-l-[3px] border-l-carbon-red-60'}`}>
          {saveMsg.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
          <span>{saveMsg.text}</span>
        </div>
      )}

      {result && result.ok && (
        <div className="text-sm bg-carbon-green-10 border border-carbon-green-50 px-4 py-3 space-y-1">
          <div className="flex items-center gap-1 text-carbon-gray-80 font-medium"><ShieldCheck size={15} className="text-carbon-green-50" /> Connection verified</div>
          <div className="text-carbon-gray-80">Company: <strong>{s(result.companyName) || '—'}</strong></div>
          <div className="text-carbon-gray-80">Collection: <strong>{s(result.collectionName) || '—'}</strong></div>
          <div className="text-carbon-gray-80">Roles: {(result.userRoles || []).length ? result.userRoles.join(', ') : '—'}</div>
          <div className="text-carbon-gray-80">Token expires: <strong>{s(result.tokenExpiration) || 'unknown'}</strong></div>
        </div>
      )}

      {result && !result.ok && (
        <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{ERROR_COPY[result.code] || result.message || 'Connection test failed.'}{result.cpscStatusCode ? ` (HTTP ${result.cpscStatusCode})` : ''}</span>
        </div>
      )}

      {/* CHA-262 — AI decoder ("Decode with AI"). Non-secret config only: the egress-consent toggle (default
          OFF — sending lab PDFs to an external LLM is tenant-data egress) + the model. The tenant LLM API key
          is provisioned OUT-OF-BAND (CHA-167 B1 pattern; the command below is copy-paste ready) and is never
          entered, displayed, or transmitted through this platform. */}
      <div className="bg-carbon-white border border-carbon-border p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-carbon-purple-70" />
          <span className="text-sm font-semibold text-carbon-ink">AI decoder ("Decode with AI")</span>
          {loaded && <CpscPill tone={savedLlmEgress ? 'green' : 'slate'} icon={savedLlmEgress ? CheckCircle2 : Lock}>{savedLlmEgress ? 'Enabled' : 'Off (default)'}</CpscPill>}
        </div>
        <p className="text-xs text-carbon-gray-70">
          Lets the upload panel send a dropped document to <em>your</em> AI provider (your key, your cost) for
          advisory sorting + prefill. Everything it produces is a suggestion the operator reviews — it never
          classifies compliance, never sets the age grade, and never submits.
        </p>
        <label className="flex items-start gap-2 text-sm text-carbon-gray-80 cursor-pointer">
          <input type="checkbox" checked={llmEgress} onChange={(e) => setLlmEgress(e.target.checked)} disabled={aiSaving} className="mt-0.5" />
          <span>
            <strong>Send documents to the AI provider</strong> — explicit consent for tenant-data egress:
            uploaded lab reports leave the platform and are sent to the configured provider when (and only
            when) an operator clicks "Decode with AI", or "Ask AI to fix" on a Manual CoC upload (only the
            flagged rows are sent).
          </span>
        </label>
        <label className="block">
          <span className="font-plex-mono text-[10px] uppercase tracking-[0.1em] font-medium text-carbon-gray-70">Model</span>
          <input
            type="text" value={llmModel} onChange={(e) => setLlmModel(e.target.value)}
            placeholder="claude-sonnet-5-5 (default)" disabled={aiSaving}
            className="mt-1 w-full text-sm border border-carbon-gray-30 bg-carbon-gray-10 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60"
          />
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[['Upload fixer model', fixModel, setFixModel], ['Retry model (once, when a fix fails the check)', fixEscalation, setFixEscalation]].map(([label, value, set]) => (
            <label key={label} className="block">
              <span className="font-plex-mono text-[10px] uppercase tracking-[0.1em] font-medium text-carbon-gray-70">{label}</span>
              <select value={value} onChange={(e) => set(e.target.value)} disabled={aiSaving} aria-label={label}
                className="mt-1 w-full text-sm border border-carbon-gray-30 bg-carbon-gray-10 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60">
                {UPLOAD_FIX_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
          ))}
        </div>
        <div className="text-[11px] text-carbon-gray-70 space-y-1">
          <div className="flex items-start gap-1"><KeyRound size={11} className="mt-0.5 shrink-0" />
            <span>One-time key provisioning (run in Cloud Shell / gcloud as an admin — the key is never entered here):</span>
          </div>
          <div className="flex items-start gap-1.5">
            <code className="block flex-1 font-plex-mono bg-carbon-gray-10 border border-carbon-border px-2 py-1.5 whitespace-pre-wrap break-all">{llmKeyCmd}</code>
            <button
              onClick={() => { navigator.clipboard?.writeText(llmKeyCmd); setCmdCopied(true); setTimeout(() => setCmdCopied(false), 2000); }}
              className="shrink-0 flex items-center gap-1 px-2 py-1 text-[11px] border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10"
              title="Copy the provisioning command"
            >
              <Copy size={11} /> {cmdCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
        <div>
          <button
            onClick={saveAiConfig}
            disabled={aiSaving}
            className="flex items-center gap-2 px-4 py-2 bg-carbon-purple-70 text-white text-sm font-semibold hover:bg-carbon-purple-60 disabled:opacity-50"
          >
            {aiSaving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save AI Settings
          </button>
        </div>
        {aiMsg && (
          <div className={`text-sm px-3 py-2 flex items-start gap-1 border ${aiMsg.ok ? 'text-carbon-gray-80 bg-carbon-green-10 border-carbon-green-50' : 'text-carbon-red-60 bg-carbon-white border-carbon-border border-l-[3px] border-l-carbon-red-60'}`}>
            {aiMsg.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
            <span>{aiMsg.text}</span>
          </div>
        )}
      </div>
    </div>
  );
}
